/**
 * Attachment Preview Dialog component
 * Extracted from Activity.tsx to improve code organization
 */

import { ReactElement, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export interface AttachmentPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  previewUrl: string;
  previewName: string;
  previewType?: string | null;
  /** When the preview link expires (ms since epoch). If set, we show a friendly message after that time. */
  previewExpiresAt?: number | null;
}

/**
 * Attachment Preview Dialog component
 */
const EXPIRED_MESSAGE = "This preview has expired. Please close and open the attachment again to view it.";
const LOAD_ERROR_MESSAGE = "The preview couldn't be loaded. The link may have expired (links are valid for 5 minutes), or the file may be unavailable. Please close and open the attachment again.";

export default function AttachmentPreviewDialog({
  open,
  onClose,
  previewUrl,
  previewName,
  previewType,
  previewExpiresAt,
}: AttachmentPreviewDialogProps): ReactElement {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState<boolean>(false);

  // Reset loading, error and expired when dialog opens or URL changes
  useEffect(() => {
    if (open && previewUrl) {
      setLoading(true);
      setError(null);
      setExpired(false);
    }
  }, [open, previewUrl]);

  // When we know expiry time, show friendly message after link expires
  useEffect(() => {
    if (!open || !previewExpiresAt || previewExpiresAt <= 0) return;
    const check = (): void => {
      if (Date.now() >= previewExpiresAt) setExpired(true);
    };
    const interval = setInterval(check, 1000);
    check();
    return () => clearInterval(interval);
  }, [open, previewExpiresAt]);

  // Determine if file is PDF based on type or URL
  const isPdf = previewType?.toLowerCase().includes("pdf") || 
                previewUrl.toLowerCase().includes(".pdf") ||
                previewName.toLowerCase().endsWith(".pdf");

  // Determine if file is an image
  const isImage = previewType?.toLowerCase().startsWith("image/") ||
                  previewUrl.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i);

  const handleImageLoad = (): void => {
    setLoading(false);
    setError(null);
  };

  const handleImageError = (): void => {
    setLoading(false);
    setError(LOAD_ERROR_MESSAGE);
  };

  const handleIframeLoad = (): void => {
    setLoading(false);
    setError(null);
  };

  const handleIframeError = (): void => {
    setLoading(false);
    setError(LOAD_ERROR_MESSAGE);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0, py: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography variant="body2" sx={{ fontSize: "0.875rem", flex: 1, minWidth: 0 }} noWrap>
            {previewName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {!expired && isPdf && (
              <Button variant="outlined" size="small" onClick={() => window.open(previewUrl, "_blank")} sx={{ flexShrink: 0 }}>
                Open in New Tab
              </Button>
            )}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {previewUrl && (
          <>
            {loading && !expired && (
              <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1 }}>
                <CircularProgress />
              </Box>
            )}
            {expired && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, p: 2 }}>
                <Alert severity="info" sx={{ maxWidth: 400 }}>
                  {EXPIRED_MESSAGE}
                  <Box sx={{ mt: 1 }}>
                    <Button variant="contained" size="small" onClick={onClose}>
                      Close
                    </Button>
                  </Box>
                </Alert>
              </Box>
            )}
            {error && !expired && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, p: 2 }}>
                <Alert severity="error" sx={{ maxWidth: 400 }}>
                  {error}
                  <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {isPdf && (
                      <Button variant="contained" size="small" onClick={() => window.open(previewUrl, "_blank")}>
                        Open in New Tab
                      </Button>
                    )}
                    <Button variant="outlined" size="small" onClick={onClose}>
                      Close
                    </Button>
                  </Box>
                </Alert>
              </Box>
            )}
            {!expired && isPdf ? (
              <Box sx={{ flex: 1, minHeight: 0, width: "100%", position: "relative", display: loading || error ? "none" : "flex", flexDirection: "column" }}>
                <iframe
                  src={previewUrl}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  title={previewName}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                />
              </Box>
            ) : !expired && isImage ? (
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", alignItems: "center", p: 2 }}>
                <img
                  src={previewUrl}
                  alt={previewName}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    display: loading || error ? "none" : "block",
                  }}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              </Box>
            ) : !expired ? (
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", p: 3 }}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Preview not available for this file type
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => window.open(previewUrl, "_blank")}
                  >
                    Open in New Tab
                  </Button>
                </Box>
              </Box>
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
